import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, Target, MessageCircle } from "lucide-react";
import imageCompression from "browser-image-compression";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function ResizeForWhatsApp({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("jpeg");
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Helper function to update SEO meta tags
  const updateSEOTags = (seo, fallbackTitle) => {
    document.title = seo?.meta_title || fallbackTitle || 'WhatsApp Image Optimizer - KayTool';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seo?.meta_description || 'Optimize images for WhatsApp with perfect 500x500px dimensions.');
    
    if (seo?.meta_keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', seo.meta_keywords);
    }
  };

  // Fetch CMS content for resize-image-for-whatsapp page
  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/resize-image-for-whatsapp`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          updateSEOTags(data.seo, data.title);
        }
      } catch (error) {
        console.log('No CMS content for resize-image-for-whatsapp page');
        updateSEOTags(null, 'WhatsApp Image Optimizer - KayTool');
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }
  }, [embedded]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const formatBytes = (bytes, decimals = 1) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Convert HEIC to JPEG if needed
  const convertHeicToJpeg = async (file) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.heic') || fileName.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif') {
      try {
        toast.info('Converting HEIC format...', { duration: 2000 });
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.92
        });
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        return new File([blob], newName, { type: 'image/jpeg' });
      } catch (error) {
        console.error('HEIC conversion error:', error);
        toast.error('Failed to convert HEIC image');
        throw error;
      }
    }
    return file;
  };

  // Compress and resize for WhatsApp (500x500 pixels)
  const compressForWhatsApp = async (file, format = outputFormat) => {
    try {
      const targetSize = 500; // 500x500 pixels
      
      // Determine file type
      let fileType = "image/jpeg";
      if (format === "png") {
        fileType = "image/png";
      }
      
      // Compression options optimized for WhatsApp profile/status
      const options = {
        maxSizeMB: 0.5, // 500KB max
        maxWidthOrHeight: targetSize,
        useWebWorker: true,
        initialQuality: 0.9,
        maxIteration: 20,
        fileType: fileType,
        alwaysKeepResolution: false,
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // Create canvas to ensure exact 500x500 dimensions with proper cropping/fitting
      const img = new Image();
      const url = URL.createObjectURL(compressedFile);
      
      return new Promise((resolve) => {
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext('2d');
          
          // Fill with white background (for transparency)
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, targetSize, targetSize);
          
          // Calculate scaling to fit image in square (contain mode)
          const scale = Math.min(targetSize / img.width, targetSize / img.height);
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          const x = (targetSize - scaledWidth) / 2;
          const y = (targetSize - scaledHeight) / 2;
          
          // Draw image centered
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            resolve({
              src: URL.createObjectURL(blob),
              blob: blob,
              size: blob.size,
              width: targetSize,
              height: targetSize,
            });
          }, fileType, 0.9);
        };
        img.src = url;
      });
    } catch (error) {
      console.error("WhatsApp compression error:", error);
      return null;
    }
  };

  const processAndCompressForWhatsApp = async (file) => {
    // Get original file info
    const originalFileName = file.name.toLowerCase();
    const wasHeic = originalFileName.endsWith('.heic') || originalFileName.endsWith('.heif') || 
                    file.type === 'image/heic' || file.type === 'image/heif';
    const originalFormat = wasHeic ? 'HEIC' : file.type.split('/')[1]?.toUpperCase() || 'Unknown';
    
    // Convert HEIC if needed
    const processableFile = await convertHeicToJpeg(file);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const img = new Image();
        img.onload = async () => {
          // Compress for WhatsApp
          const compressed = await compressForWhatsApp(processableFile);
          
          resolve({
            id: Math.random().toString(36).substr(2, 9),
            name: processableFile.name,
            originalSrc: e.target.result,
            originalSize: file.size,
            originalWidth: img.width,
            originalHeight: img.height,
            originalFormat,
            wasConverted: wasHeic,
            file: processableFile,
            compressed,
          });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(processableFile);
    });
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    
    setIsProcessing(true);
    setCompressionProgress(0);
    
    const fileArray = Array.from(files).filter(file => 
      file.type.startsWith('image/') || 
      file.name.toLowerCase().endsWith('.heic') || 
      file.name.toLowerCase().endsWith('.heif')
    );
    
    if (fileArray.length === 0) {
      toast.error("Please select valid image files");
      setIsProcessing(false);
      return;
    }

    const processedImages = [];
    for (let i = 0; i < fileArray.length; i++) {
      const result = await processAndCompressForWhatsApp(fileArray[i]);
      processedImages.push(result);
      setCompressionProgress(Math.round(((i + 1) / fileArray.length) * 100));
    }
    
    setImages(prev => [...prev, ...processedImages]);
    setIsProcessing(false);
    setCompressionProgress(0);
    toast.success(`${fileArray.length} image${fileArray.length > 1 ? 's' : ''} optimized for WhatsApp!`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDownloadSingle = (image) => {
    if (!image.compressed) return;
    
    const link = document.createElement('a');
    link.href = image.compressed.src;
    const extension = outputFormat === 'png' ? 'png' : 'jpg';
    const baseName = image.name.replace(/\.[^.]+$/, '');
    link.download = `${baseName}_whatsapp.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const handleDownloadAll = async () => {
    if (images.length === 0) return;
    
    if (images.length === 1) {
      handleDownloadSingle(images[0]);
      return;
    }
    
    const zip = new JSZip();
    const extension = outputFormat === 'png' ? 'png' : 'jpg';
    
    for (const image of images) {
      if (image.compressed) {
        const response = await fetch(image.compressed.src);
        const blob = await response.blob();
        const baseName = image.name.replace(/\.[^.]+$/, '');
        zip.file(`${baseName}_whatsapp.${extension}`, blob);
      }
    }
    
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'whatsapp_images.zip');
    toast.success('All images downloaded!');
  };

  const handleRemoveImage = (imageId) => {
    setImages(images.filter(img => img.id !== imageId));
  };

  const handleClearAll = () => {
    setImages([]);
    toast.info("All images cleared");
  };

  const handleAddMoreImages = () => {
    addMoreInputRef.current?.click();
  };

  // Handle format change - recompress all images
  const handleFormatChange = async (newFormat) => {
    setOutputFormat(newFormat);
    
    if (images.length > 0) {
      setIsProcessing(true);
      setCompressionProgress(0);
      
      const updatedImages = [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const compressed = await compressForWhatsApp(img.file, newFormat);
        updatedImages.push({ ...img, compressed });
        setCompressionProgress(Math.round(((i + 1) / images.length) * 100));
      }
      
      setImages(updatedImages);
      setIsProcessing(false);
      setCompressionProgress(0);
      toast.success(`Converted to ${newFormat.toUpperCase()}`);
    }
  };

  // Handle paste from clipboard
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      const imageFiles = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      
      if (imageFiles.length > 0) {
        handleFileSelect(imageFiles);
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Calculate totals
  const totalOriginal = images.reduce((sum, img) => sum + img.originalSize, 0);
  const totalCompressed = images.reduce((sum, img) => sum + (img.compressed?.size || 0), 0);
  const totalSaved = totalOriginal > 0 ? Math.round((1 - totalCompressed / totalOriginal) * 100) : 0;

  if (embedded) {
    return (
      <div className="w-full">
        {/* Embedded version - simplified */}
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`h-40 border-2 border-dashed rounded-xl cursor-pointer ${
            isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'
          } bg-white hover:bg-slate-50 flex flex-col items-center justify-center gap-2 transition-colors`}
        >
          <MessageCircle className="w-8 h-8 text-emerald-500" />
          <p className="text-sm text-slate-600">Drop images for WhatsApp</p>
          <Badge className="bg-emerald-500 text-white">500×500px</Badge>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 via-emerald-50/30 to-green-50/50'}`}>
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">WhatsApp Compress</span>
                <span className="text-xs text-slate-500 dark:text-slate-400 block">by KayTool</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <ToolsNav />
              {images.length > 0 && (
                <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700">
                  {images.length} image{images.length > 1 ? 's' : ''} optimized
                </Badge>
              )}
              <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <Zap className="w-3 h-3 mr-1" />
                500×500px
              </Badge>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Upload Zone */}
        {images.length === 0 && (
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {pageContent?.title || <>Optimize for <span className="text-emerald-500">WhatsApp</span></>}
              </h2>
              <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                {pageContent?.seo?.meta_description || "Resize and compress images to perfect 500×500 pixels for WhatsApp profile pictures and status."}
              </p>
            </div>
            
            <div
              data-testid="upload-zone"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`upload-zone h-64 md:h-80 border-4 border-dashed rounded-xl cursor-pointer ${
                isDragging ? 'drag-over border-emerald-500' : 'border-slate-300 dark:border-slate-600'
              } bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex flex-col items-center justify-center gap-4 transition-colors duration-300`}
            >
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Upload className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                  Drop, paste, or click to upload
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Ctrl+V to paste from clipboard • Multiple files supported
                </p>
              </div>
              <Badge className="bg-emerald-500 text-white px-4 py-1">
                Auto-resize to 500×500px
              </Badge>
              <input
                ref={fileInputRef}
                data-testid="file-input"
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-2">
                    <MessageCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">WhatsApp Ready</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Perfect for profile & status</p>
                </CardContent>
              </Card>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-2">
                    <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">500×500 Pixels</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Optimal square dimensions</p>
                </CardContent>
              </Card>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardContent className="p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-2">
                    <Info className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">100% Private</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Browser-only processing</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Results */}
        {images.length > 0 && (
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Format Selection & Stats */}
            <Card className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
              <ToolsNav />
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Output Format</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Choose JPG or PNG</p>
                    </div>
                    <Select value={outputFormat} onValueChange={handleFormatChange}>
                      <SelectTrigger className="w-32 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="jpeg">JPEG/JPG</SelectItem>
                        <SelectItem value="png">PNG</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">ORIGINAL</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">{formatBytes(totalOriginal)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">COMPRESSED</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatBytes(totalCompressed)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">SAVED</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{totalSaved}%</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Processing Progress */}
            {isProcessing && (
              <Card className="border-emerald-200 dark:border-emerald-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
              <ToolsNav />
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        Optimizing for WhatsApp...
                      </p>
                      <Progress value={compressionProgress} className="mt-2 h-2" />
                    </div>
                    <span className="text-sm text-slate-500">{compressionProgress}%</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="download-all-button"
                  onClick={handleDownloadAll}
                  disabled={isProcessing}
                  className="rounded-full px-6 py-6 shadow-lg shadow-emerald-500/20 font-bold bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {images.length === 1 ? 'Download' : `Download All (${images.length})`}
                </Button>
                <Button
                  data-testid="clear-all-button"
                  onClick={handleClearAll}
                  disabled={isProcessing}
                  variant="outline"
                  className="rounded-full px-6 py-6 border-2 border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                >
                  <X className="w-4 h-4 mr-2" />
                  {images.length === 1 ? 'Clear' : 'Clear All'}
                </Button>
                <Button
                  onClick={handleAddMoreImages}
                  variant="outline"
                  className="rounded-full px-6 py-6 border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Add More
                </Button>
              </div>
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <div className="relative aspect-square bg-slate-100 dark:bg-slate-900">
                    <img
                      src={image.compressed?.src || image.originalSrc}
                      alt={image.name}
                      className="w-full h-full object-contain"
                    />
                    <Badge className="absolute top-2 right-2 bg-emerald-500 text-white text-xs">
                      {image.compressed ? formatBytes(image.compressed.size) : '...'}
                    </Badge>
                    <Badge className="absolute top-2 left-2 bg-slate-800/80 text-white text-xs">
                      500×500
                    </Badge>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate mb-2">
                      {image.name}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
                      <span>{formatBytes(image.originalSize)} → {image.compressed ? formatBytes(image.compressed.size) : '...'}</span>
                      {image.compressed && (
                        <Badge variant="secondary" className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                          -{Math.round((1 - image.compressed.size / image.originalSize) * 100)}%
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDownloadSingle(image)}
                        disabled={!image.compressed}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveImage(image.id)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content - After Tool (Same style as homepage) */}
      {pageContent?.components?.length > 0 && (
        <section className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 transition-colors duration-300">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <RenderCMSContent components={pageContent.components} />
            </div>
          </div>
        </section>
      )}

      {/* Hidden file input for adding more images */}
      <input
        ref={addMoreInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />


      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 mt-auto">
        <div className="container mx-auto px-4 py-6">
          <div className="text-center text-sm text-slate-600 dark:text-slate-400">
            All processing happens locally in your browser. Your images are never uploaded to any server.
          </div>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default ResizeForWhatsApp;
